/**
 * A modal notice: a scrim over the whole window, a card with a heading and a line of
 * explanation, and one button that dismisses it.
 *
 * Real DOM rather than Pixi, by design/UI.md's dividing question — it is chrome that explains
 * something about the *session*, not something happening in the sea, and it wants a focusable
 * button, a keyboard default and text that reflows. The toolbar and the growth corner are DOM
 * for the same reason.
 *
 * It exists because a connection can die, and until now nothing said so: the tab kept rendering
 * the last state it decoded, the sea froze, and the player was left steering a still world with
 * no way to tell a lagging arena from a dead one. See design/Networking.md.
 *
 * **It blocks the sea.** The scrim takes the pointer, so a click meant for the button cannot fall
 * through and lay the guns on whatever is behind it. That is why it is a scrim at all rather than
 * a card floating in a corner — a notice the player can sail away from without answering is a
 * notice they will sail away from without reading.
 */

/** What the button says when the caller does not name one. */
const DEFAULT_CONFIRM = "Back to port";

export class Notice {
    private readonly _root = document.createElement("div");
    private readonly _card = document.createElement("div");
    private readonly _title = document.createElement("h2");
    private readonly _body = document.createElement("p");
    private readonly _confirm = document.createElement("button");

    /**
     * Resolver of the promise the open notice handed out, or null when nothing is showing. The
     * promise is the whole interface: a caller `await`s the player reading the thing before it
     * does whatever the notice was warning them about.
     */
    private _resolve: (() => void) | null = null;

    public get visible(): boolean {
        return this._resolve !== null;
    }

    constructor() {
        this._root.className = "notice";
        this._root.hidden = true;
        // A dialog to anything reading the page rather than a div that happens to be on top:
        // the role and the label are what a screen reader has to go on, and `aria-modal` is
        // what tells it the rest of the page is not currently reachable.
        this._root.setAttribute("role", "dialog");
        this._root.setAttribute("aria-modal", "true");

        this._card.className = "notice-card";
        this._title.className = "notice-title";
        this._body.className = "notice-body";

        this._confirm.className = "notice-confirm";
        this._confirm.type = "button";
        this._confirm.textContent = DEFAULT_CONFIRM;
        this._confirm.addEventListener("click", () => this._close());

        this._card.append(this._title, this._body, this._confirm);
        this._root.append(this._card);
    }

    public mount(): void {
        document.body.appendChild(this._root);
    }

    /**
     * Puts the notice up and resolves when the player dismisses it.
     *
     * The button takes focus on the way in, so Enter or Space answers without the mouse — which
     * matters here more than it looks: the notice this class exists for goes up at the moment the
     * player has just lost their connection, and hunting for a button with a pointer is the last
     * thing that moment needs.
     *
     * A second `show` while one is open resolves the first rather than stacking or being dropped.
     * Two notices at once is not a state this game has, and silently swallowing the second would
     * hide whichever problem arrived last.
     */
    public show(title: string, body: string, confirmLabel: string = DEFAULT_CONFIRM): Promise<void> {
        this._resolve?.();

        this._title.textContent = title;
        this._body.textContent = body;
        this._confirm.textContent = confirmLabel;
        this._root.hidden = false;
        this._confirm.focus();

        return new Promise<void>((resolve) => {
            this._resolve = resolve;
        });
    }

    public destroy(): void {
        this._close();
        this._root.remove();
    }

    /** Takes it down and releases whoever was waiting. Safe to call when nothing is showing. */
    private _close(): void {
        this._root.hidden = true;

        const resolve = this._resolve;
        this._resolve = null;
        resolve?.();
    }
}
